import React, { useState, useEffect } from "react";
import { Tab, TabList, Button, Spinner, Text } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Add20Filled, Edit20Regular, Diagram20Regular, Dismiss20Regular, Save20Regular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";

import ResourcesAccountChatComponent from "./ResourcesAccountChatComponent";
import GreetingChatComponent from "./GreetingChatComponent";
import RoutingChatComponent from "./RoutingChatComponent";

import { emptyChatFlowObject } from "../GenericComponent/GenericData/emptyChatFlowObject";
import { GetChatFlow, AddChatFlow, UpdateChatFlow } from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from '../GenericComponent/NotificationSystem';

import "../CSS/CallFlowsComponent.css";
import "../CSS/ChatFlowsComponent.css";

export default function ChatFlowsComponent({ st_ChatFlows, st_WhatsAppFlows, setChatFlows, setWhatsAppFlows, ACTIVE_TENANT, msalInstance, Token, CALL_FLOWS, SCOPES }) {
  const { t } = useTranslation();
  const { chatFlowId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();

  const isCreateMode = chatFlowId === "newChatFlow";
  const isEditMode = !isCreateMode;
  const isWhatsAppRoute = location.pathname.startsWith("/whatsapp");

  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState("resourcesAccount");
  const [chatFlowData, setChatFlowData] = useState(emptyChatFlowObject);
  const [loadFailed, setLoadFailed] = useState(false);
  
  
  useEffect(() => {
    if (chatFlowId) {
      loadChatFlow();
    }
  }, [chatFlowId]);

  useEffect(() => {
    if (!ability) return;

    const flowScope = chatFlowData?.scope || "default";
    
    
    const permissions = {
      main: {
        canCreate: ability.can("create", `Flow.main@${flowScope}`),
        canRead: ability.can("read", `Flow.main@${flowScope}`),
        canUpdate: ability.can("update", `Flow.main@${flowScope}`),
        canDelete: ability.can("delete", `Flow.main@${flowScope}`),
      },
      resourceAccount: {
        canCreate: ability.can("create", `Flow.resourceAccount@${flowScope}`),
        canRead: ability.can("read", `Flow.resourceAccount@${flowScope}`),
        canUpdate: ability.can("update", `Flow.resourceAccount@${flowScope}`),
        canDelete: ability.can("delete", `Flow.resourceAccount@${flowScope}`),
      },
      greeting: {
        canCreate: ability.can("create", `Flow.greetingState@${flowScope}`),
        canRead: ability.can("read", `Flow.greetingState@${flowScope}`),
        canUpdate: ability.can("update", `Flow.greetingState@${flowScope}`),
        canDelete: ability.can("delete", `Flow.greetingState@${flowScope}`),
        messages: {
          canCreate: ability.can("create", `Flow.messages@${flowScope}`),
          canRead: ability.can("read", `Flow.messages@${flowScope}`),
          canUpdate: ability.can("update", `Flow.messages@${flowScope}`),
          canDelete: ability.can("delete", `Flow.messages@${flowScope}`),
        },
      },
      routing: {
        canCreate: ability.can("create", `Flow.routing@${flowScope}`),
        canRead: ability.can("read", `Flow.routing@${flowScope}`),
        canUpdate: ability.can("update", `Flow.routing@${flowScope}`),
        canDelete: ability.can("delete", `Flow.routing@${flowScope}`),
      },
    };


    setChatFlowData((prev) => ({ ...prev, permissions }));
  }, [ability, chatFlowData?.scope, chatFlowData?.id]);

  const loadChatFlow = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      if (isEditMode) {
        const existingFlow = st_ChatFlows?.find((f) => f.id === chatFlowId);
        if (existingFlow) {
          setChatFlowData({ ...emptyChatFlowObject, ...existingFlow });
        } else {
          const token = Token || (await getAccessToken(instance, activeAccount));
          const scope = "default";
          const response = await GetChatFlow(ACTIVE_TENANT, userId, chatFlowId, scope, token);
          if (response) {
            setChatFlowData({ ...emptyChatFlowObject, ...response });
          } else {
            setLoadFailed(true);
          }
        }
      } else {
        // Mode création - initialiser avec le premier CallFlow si disponible
        const initialData = { ...emptyChatFlowObject };
        if (CALL_FLOWS && CALL_FLOWS.length > 0) {
          initialData.attachedFlowId = {
            id: CALL_FLOWS[0].id,
            displayName: CALL_FLOWS[0].displayName
          };
        }
        setChatFlowData(initialData);
      }
    } catch (error) {
      console.error("Error loading chat flow:", error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    setSaving(true);
    
    getAccessToken(instance, activeAccount)
      .then((token) => {
        const scope = chatFlowData.scope || "default";

        // Préparer les données pour la sauvegarde avec la nouvelle structure
        const { workflow, survey, permissions, botId, botKey, dataLocation, region, callFlowLink, ...rest } = chatFlowData;
        
        
        // Construire l'objet selon le nouveau format
        const chatFlowToSave = {
          id: isCreateMode && chatFlowData.botId ? chatFlowData.botId : chatFlowData.id,
          displayName: chatFlowData.displayName,
          type: "chat",
          timeZone: chatFlowData.timeZone,
          language: chatFlowData.language || "",
          scope: chatFlowData.scope,
          attachedFlowId: chatFlowData.attachedFlowId || {
            id: "",
            displayName: ""
          },
          greeting: {
            emergencyMessage: {
              enabled: chatFlowData.greeting?.emergencyMessage?.enabled || false,
              text: chatFlowData.greeting?.emergencyMessage?.text || ""
            },
            welcomeMessage: {
              enabled: chatFlowData.greeting?.welcomeMessage?.enabled || false,
              text: chatFlowData.greeting?.welcomeMessage?.text || ""
            },
            flashMessage: {
              enabled: chatFlowData.greeting?.flashMessage?.enabled || false,
              text: chatFlowData.greeting?.flashMessage?.text || ""
            },
            customMessage: {
              enabled: chatFlowData.greeting?.customMessage?.enabled || false,
              text: chatFlowData.greeting?.customMessage?.text || ""
            }
          },
          deskOpen: {
            holidays: {
              daysOff: chatFlowData.deskOpen?.holidays?.daysOff || [],
              weekendDays: chatFlowData.deskOpen?.holidays?.weekendDays || [6, 0],
              holidaysAction: chatFlowData.deskOpen?.holidays?.holidaysAction || {
                type: "disconnect",
                targetType: "",
                targetId: "",
                queueId: "",
                queueDisplayName: ""
              }
            },
            openHours: {
              "0": chatFlowData.deskOpen?.openHours?.["0"] || [{ start: "00:00", end: "23:59" }],
              "1": chatFlowData.deskOpen?.openHours?.["1"] || [{ start: "00:00", end: "23:59" }],
              "2": chatFlowData.deskOpen?.openHours?.["2"] || [{ start: "00:00", end: "23:59" }],
              "3": chatFlowData.deskOpen?.openHours?.["3"] || [{ start: "00:00", end: "23:59" }],
              "4": chatFlowData.deskOpen?.openHours?.["4"] || [{ start: "00:00", end: "23:59" }],
              "5": chatFlowData.deskOpen?.openHours?.["5"] || [{ start: "00:00", end: "23:59" }],
              "6": chatFlowData.deskOpen?.openHours?.["6"] || [{ start: "00:00", end: "23:59" }],
              openHoursAction: chatFlowData.deskOpen?.openHours?.openHoursAction || {
                type: "disconnect",
                targetType: "",
                targetId: "",
                queueId: "",
                queueDisplayName: ""
              }
            }
          },
          routing: chatFlowData.routing || {
            "0": {
              type: "",
              customerChoiceName: "",
              customerChoiceText: "",
              queueDisplayName: "",
              queueId: "",
              customerChoiceActions: {}
            }
          }
        };

        // Ajouter le password seulement s'il est rempli
        if (chatFlowData.botKey && chatFlowData.botKey.trim() !== "") {
          chatFlowToSave.password = chatFlowData.botKey;
        }

        // Ajouter les champs spécifiques WhatsApp s'ils existent
        if (chatFlowData.whatsAppId) {
          chatFlowToSave.whatsAppId = chatFlowData.whatsAppId;
        }
        if (chatFlowData.teamsBotId) {
          chatFlowToSave.teamsBotId = chatFlowData.teamsBotId;
        }
        if (chatFlowData.token) {
          chatFlowToSave.token = chatFlowData.token;
        }

        if (isCreateMode) {
          return AddChatFlow(ACTIVE_TENANT, userId, scope, token, chatFlowToSave)
            .then((result) => {
              if (result?.success === true) {
                // Vérifier si le ChatFlow a été créé avec succès
                const createdChatFlow = result.chatFlow || result;
                
                // Séparer selon le type de flow
                const isWhatsApp = createdChatFlow.whatsAppId && createdChatFlow.teamsBotId;
                if (isWhatsApp) {
                  const updatedFlows = [...(st_WhatsAppFlows || []), createdChatFlow];
                  setWhatsAppFlows(updatedFlows);
                  navigate(`/whatsapp`);
                } else {
                  const updatedFlows = [...(st_ChatFlows || []), createdChatFlow];
                  setChatFlows(updatedFlows);
                  navigate(`/chatFlows`);
                }
                
                showSuccess(t("CHATFLOW_CREATED_SUCCESS"));
                return result; // ✅ Retourner pour éviter le catch
              } else {
                const errorMsg = result?.message || "Erreur inconnue";
                console.error("Échec création ChatFlow:", result);
                showError(t("CHATFLOW_CREATION_FAILED"), errorMsg);
                throw new Error(errorMsg); // ✅ Rejeter la promise pour que catch ne soit pas appelé
              }
            });
        } else {
          return UpdateChatFlow(ACTIVE_TENANT, userId, chatFlowId, scope, token, chatFlowToSave)
            .then((result) => {
              if (result?.success === true) {
                // Vérifier si le ChatFlow a été mis à jour avec succès
                const updatedChatFlow = result.chatFlow || result;
                
                // Séparer selon le type de flow
                const isWhatsApp = updatedChatFlow.whatsAppId && updatedChatFlow.teamsBotId;
                if (isWhatsApp) {
                  const updatedFlows = (st_WhatsAppFlows || []).map((f) => (f.id === chatFlowId ? updatedChatFlow : f));
                  setWhatsAppFlows(updatedFlows);
                  navigate(`/whatsapp`);
                } else {
                  const updatedFlows = (st_ChatFlows || []).map((f) => (f.id === chatFlowId ? updatedChatFlow : f));
                  setChatFlows(updatedFlows);
                  navigate(`/chatFlows`);
                }
                
                showSuccess(t("CHATFLOW_UPDATED_SUCCESS"));
                return result; // ✅ Retourner pour éviter le catch
              } else {
                const errorMsg = result?.message || "Erreur inconnue";
                console.error("Échec mise à jour ChatFlow:", result);
                showError(t("CHATFLOW_UPDATE_FAILED"), errorMsg);
                throw new Error(errorMsg); // ✅ Rejeter la promise pour que catch ne soit pas appelé
              }
            });
        }
      })
      .catch((error) => {
        console.error("Error saving chat flow:", error);
        showError(isCreateMode ? t("CHATFLOW_CREATION_ERROR") : t("CHATFLOW_UPDATE_ERROR"), error?.message);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const canSave = isCreateMode
    ? chatFlowData.permissions?.main?.canCreate
    : chatFlowData.permissions?.main?.canUpdate;

  const isFormValid = isCreateMode 
    ? chatFlowData.botId && chatFlowData.displayName && chatFlowData.botKey && chatFlowData.scope
    : chatFlowData.displayName && chatFlowData.scope;

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="tab-loading">
          <Spinner label={t("LOADING")} size="large" />
        </div>
      );
    }

    if (loadFailed) {
      return (
        <div className="tab-error">
          <Text weight="semibold">{t("ERROR_LOADING_CHAT_FLOW")}</Text>
        </div>
      );
    }

    switch (selectedTab) {
      case "resourcesAccount":
        return <ResourcesAccountChatComponent chatFlowData={chatFlowData} setChatFlowData={setChatFlowData} isEditMode={isEditMode} permissions={chatFlowData.permissions} st_CallFlows={CALL_FLOWS} SCOPES={SCOPES} isWhatsAppRoute={isWhatsAppRoute} />;
      case "greeting":
        return <GreetingChatComponent chatFlowData={chatFlowData} setChatFlowData={setChatFlowData} isEditMode={isEditMode} permissions={chatFlowData.permissions} />;
      case "routing":
        return <RoutingChatComponent chatFlowData={chatFlowData} setChatFlowData={setChatFlowData} isEditMode={isEditMode} ACTIVE_TENANT={ACTIVE_TENANT} permissions={chatFlowData.permissions} />;
      default:
        return null;
    }
  };

  return (
    <div className="chatflows-container">
      {!loading && loadFailed && (
        <div className="top-error">
          <Text weight="semibold">{t("CHAT_FLOW_NOT_FOUND")}</Text>
        </div>
      )}

      <div className="header-wrapper">
        <TabList selectedValue={selectedTab} onTabSelect={(_, data) => setSelectedTab(data.value)} className="tab-list">
          {chatFlowData.permissions?.resourceAccount?.canRead && (
            <Tab value="resourcesAccount">{t("RESOURCES_ACCOUNT")}</Tab>
          )}
          
          {isEditMode && (
            <>
              {chatFlowData.permissions?.greeting?.canRead && (
                <Tab value="greeting">{t("GREETING_MESSAGES")}</Tab>
              )}
              
              {chatFlowData.permissions?.routing?.canRead && (
                <Tab value="routing">{t("ROUTING")}</Tab>
              )}
            </>
          )}
        </TabList>

        <div className="action-buttons">
          
          <Button
            icon={<Dismiss20Regular />}
            appearance="secondary"
            onClick={() => navigate(isWhatsAppRoute ? "/whatsapp" : "/chatFlows")}
            style={{ marginRight: '10px' }}
          >
            {t("CANCEL")}
          </Button>
          <Button
            icon={isEditMode ? <Edit20Regular /> : <Save20Regular />}
            appearance="primary"
            onClick={handleSave}
            disabled={saving || !canSave || !isFormValid}
          >
            {t("SAVE")}
          </Button>
        </div>
      </div>

      <div className="chatflow-tab-content">{renderTabContent()}</div>
    </div>
  );
}
